import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Target,
  Layers,
  Play,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  BarChart3,
  Download,
  Eye,
  Pause,
  Cpu,
  MemoryStick,
} from 'lucide-react';
import { TAO6Experiment, TAO6TaskType } from '@/mock/gpu-instance';

interface TAO6TrainingMonitorProps {
  experiments: TAO6Experiment[];
  onViewDetails?: (experiment: TAO6Experiment) => void;
  onExport?: (experiment: TAO6Experiment) => void;
}

const getTaskIcon = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return <Target className="h-4 w-4 text-green-500" />;
    case 'image_classification':
      return <Layers className="h-4 w-4 text-blue-500" />;
  }
};

const getStatusBadge = (status: TAO6Experiment['status']) => {
  switch (status) {
    case 'training':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <Play className="h-3 w-3 mr-1" /> Training
        </Badge>
      );
    case 'completed':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
        </Badge>
      );
    case 'failed':
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
          <AlertCircle className="h-3 w-3 mr-1" /> Failed
        </Badge>
      );
    case 'queued':
      return (
        <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
          <Clock className="h-3 w-3 mr-1" /> Queued
        </Badge>
      );
    case 'evaluating':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <BarChart3 className="h-3 w-3 mr-1" /> Evaluating
        </Badge>
      );
    case 'exporting':
      return (
        <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Exporting
        </Badge>
      );
  }
};

const formatTime = (timestamp: number): string => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const formatDuration = (startTime: number, endTime?: number): string => {
  const end = endTime || Date.now();
  const diff = end - startTime;
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const TAO6TrainingMonitor = ({
  experiments,
  onViewDetails,
  onExport,
}: TAO6TrainingMonitorProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const trainingExperiments = experiments.filter((e) => e.status === 'training');
  const completedExperiments = experiments.filter((e) => e.status === 'completed');
  const queuedExperiments = experiments.filter((e) => e.status === 'queued');

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{trainingExperiments.length}</div>
            <div className="text-sm text-muted-foreground">Training</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{completedExperiments.length}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{queuedExperiments.length}</div>
            <div className="text-sm text-muted-foreground">Queued</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{experiments.length}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </CardContent>
        </Card>
      </div>

      {/* Experiments List */}
      <div className="space-y-4">
        {experiments.map((experiment) => (
          <Card
            key={experiment.id}
            className={`transition-all ${
              experiment.status === 'training' ? 'border-blue-500/50' : ''
            }`}
          >
            <CardContent className="pt-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getTaskIcon(experiment.task)}
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {experiment.name}
                      {getStatusBadge(experiment.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Model: {experiment.pretrainedModel.displayName} | Dataset: {experiment.dataset.name}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {experiment.status === 'completed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onExport?.(experiment)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Export
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onViewDetails?.(experiment)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Details
                  </Button>
                </div>
              </div>

              {/* Progress Bar (for training) */}
              {(experiment.status === 'training' || experiment.status === 'evaluating') && (
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">
                      Epoch {experiment.metrics.currentEpoch}/{experiment.metrics.totalEpochs}
                      <span className="ml-2 text-muted-foreground">({experiment.progress}%)</span>
                    </span>
                  </div>
                  <Progress value={experiment.progress} className="h-2" />
                  {experiment.training.estimatedCompletion && (
                    <div className="text-xs text-muted-foreground text-right">
                      Est. completion: {formatTime(experiment.training.estimatedCompletion)}
                    </div>
                  )}
                </div>
              )}

              {/* Metrics Grid */}
              <div className="grid grid-cols-4 md:grid-cols-6 gap-3 mb-4">
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Train Loss</div>
                  <div className="font-mono font-medium text-sm">
                    {experiment.metrics.trainLoss.toFixed(4)}
                  </div>
                </div>
                {experiment.metrics.valLoss !== undefined && (
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="text-xs text-muted-foreground">Val Loss</div>
                    <div className="font-mono font-medium text-sm">
                      {experiment.metrics.valLoss.toFixed(4)}
                    </div>
                  </div>
                )}
                {experiment.metrics.accuracy !== undefined && (
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="text-xs text-muted-foreground">Accuracy</div>
                    <div className="font-mono font-medium text-sm text-green-600">
                      {(experiment.metrics.accuracy * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
                {experiment.metrics.mAP !== undefined && (
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="text-xs text-muted-foreground">mAP</div>
                    <div className="font-mono font-medium text-sm text-green-600">
                      {(experiment.metrics.mAP * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
                {experiment.metrics.precision !== undefined && (
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="text-xs text-muted-foreground">Precision</div>
                    <div className="font-mono font-medium text-sm">
                      {(experiment.metrics.precision * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
                {experiment.metrics.recall !== undefined && (
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="text-xs text-muted-foreground">Recall</div>
                    <div className="font-mono font-medium text-sm">
                      {(experiment.metrics.recall * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>

              {/* GPU Usage (for training) */}
              {experiment.status === 'training' && experiment.training.gpuUtilization > 0 && (
                <div className="flex items-center gap-4 p-2 bg-muted/30 rounded text-sm">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-green-500" />
                    <span className="text-muted-foreground">GPU:</span>
                    <span className="font-mono">{experiment.training.gpuUtilization}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MemoryStick className="h-4 w-4 text-blue-500" />
                    <span className="text-muted-foreground">VRAM:</span>
                    <span className="font-mono">{experiment.training.gpuMemoryUsed.toFixed(1)} GB</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-mono">{formatDuration(experiment.training.startedAt)}</span>
                  </div>
                </div>
              )}

              {/* Exports (for completed) */}
              {experiment.exports.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="text-sm font-medium mb-2">Exported Models</div>
                  <div className="flex flex-wrap gap-2">
                    {experiment.exports.map((exp) => (
                      <Badge key={exp.id} variant="secondary" className="text-xs">
                        <Download className="h-3 w-3 mr-1" />
                        {exp.format.toUpperCase()} {exp.size && `(${exp.size})`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Config Tags */}
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge variant="outline" className="text-xs">
                  {experiment.config.epochs} epochs
                </Badge>
                <Badge variant="outline" className="text-xs">
                  batch {experiment.config.batchSize}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  lr {experiment.config.learningRate}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {experiment.config.optimizer}
                </Badge>
                {experiment.config.enableQAT && (
                  <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30 text-xs">
                    QAT
                  </Badge>
                )}
                {experiment.config.enableKD && (
                  <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30 text-xs">
                    KD
                  </Badge>
                )}
                {experiment.config.multiGPU && (
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-xs">
                    {experiment.config.gpuCount} GPUs
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {experiments.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="pt-6 text-center">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No experiments yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first experiment to start training
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TAO6TrainingMonitor;
